import { createFileRoute } from "@tanstack/react-router"
import { Button, cn } from "@aix/ui"
import type { CSSProperties } from "react"
import { ReactElement } from "react"
import MarketingLayout from "~/layouts/MarketingLayout"
import MarketingFAQ from "~/components/Marketing/MarketingFAQ"
import CustomHead from "~/components/SEOHead/CustomHead"
import { getPageSEO } from "~/utils/seo"

function HowItWorks() {
  // Basic visibility hook logic, can be expanded later
  const isVisible = true // Placeholder

  const howItWorksSEO = getPageSEO("how-it-works")

  return (
    <>
      <CustomHead
        title={howItWorksSEO.title}
        description={howItWorksSEO.description}
        keywords={howItWorksSEO.keywords}
        pageType={howItWorksSEO.pageType}
        url="https://vidmax.ai/how-it-works"
      />

      <div className="min-h-screen text-foreground">
        {/* Main Content */}
        <div className="flex flex-col pt-3 lg:pt-24">
          {/* Hero Section */}
          <section
            className={cn(
              "opacity-0 transition-all ease-out duration-1000 translate-y-20 min-h-[calc(60dvh_-_53px)] flex items-center justify-center text-center", // Added justify-center and text-center
              "bg-gradient-to-br from-purple-900 via-background to-background", // Gradient background example
              { "opacity-100 translate-y-0": isVisible }, // Basic animation trigger
            )}
          >
            <div className="max-w-3xl px-4">
              <h1 className="text-4xl font-bold tracking-tight sm:text-6xl">Idea in. Viral video out.</h1>
              <p className="mt-6 text-lg leading-8 text-muted-foreground">
                See how VidMax turns raw thoughts into thumb-stopping clips—no editing required.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <Button
                  size="lg"
                  onClick={() => {
                    /* noop */
                  }}
                >
                  Start Free →
                </Button>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => {
                    /* noop */
                  }}
                >
                  Watch 60-sec demo
                </Button>
              </div>
              {/* Placeholder for floating video thumbnails */}
              <div className="mt-12 text-xs text-muted-foreground">←motion-note→ Subtle parallax here</div>
            </div>
          </section>

          {/* 3-Step How It Works */}
          <section className="py-16 sm:py-24">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <h2 className="text-center text-3xl font-bold tracking-tight sm:text-4xl">How VidMax Works</h2>
              <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
                {/* Step 1 */}
                <div
                  className={cn(
                    "opacity-0 transition-all ease-out duration-1000 translate-y-20 delay-100 rounded-lg border border-border bg-secondary p-6",
                    { "opacity-100 translate-y-0": isVisible },
                  )}
                >
                  <h3 className="text-xl font-semibold">① Pick a vibe</h3>
                  <p className="mt-2 text-muted-foreground">
                    Draft a prompt or choose from 100+ templates designed for virality.
                  </p>
                  <div className="mt-4 text-xs text-muted-foreground">←motion-note→ Slide-in on scroll</div>
                </div>
                {/* Step 2 */}
                <div
                  className={cn(
                    "opacity-0 transition-all ease-out duration-1000 translate-y-20 delay-200 rounded-lg border border-border bg-secondary p-6",
                    { "opacity-100 translate-y-0": isVisible },
                  )}
                >
                  <h3 className="text-xl font-semibold">② Let AI do the heavy lift</h3>
                  <p className="mt-2 text-muted-foreground">
                    Auto-generate script, voiceover, visuals, and captions in seconds. Tweak if needed.
                  </p>
                  <div className="mt-4 text-xs text-muted-foreground">←motion-note→ Slide-in on scroll</div>
                </div>
                {/* Step 3 */}
                <div
                  className={cn(
                    "opacity-0 transition-all ease-out duration-1000 translate-y-20 delay-300 rounded-lg border border-border bg-secondary p-6",
                    { "opacity-100 translate-y-0": isVisible },
                  )}
                >
                  <h3 className="text-xl font-semibold">③ Hit publish everywhere</h3>
                  <p className="mt-2 text-muted-foreground">
                    One-click scheduling posts your video to TikTok, YouTube Shorts, and Instagram Reels.
                  </p>
                  <div className="mt-4 text-xs text-muted-foreground">←motion-note→ Slide-in on scroll</div>
                </div>
              </div>
            </div>
          </section>

          {/* Live Walkthrough GIF */}
          <section className="py-16 sm:py-24 bg-secondary">
            <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">See it Live</h2>
              <div className="mt-8 aspect-video w-full rounded-lg border border-border bg-muted">
                {/* Placeholder for GIF/Video */}
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  [Walkthrough GIF/Video Player Here]
                  <br />
                  ←motion-note→ Auto-plays when 70% in viewport
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">Real-time: Idea to publish in 41 seconds</p>
            </div>
          </section>

          {/* Feature Highlights */}
          <section className="py-16 sm:py-24">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <h2 className="text-center text-3xl font-bold tracking-tight sm:text-4xl">
                Everything You Need Included
              </h2>
              <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3 text-center">
                {/* Feature 1 */}
                <div>
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {/* Placeholder Icon */} T
                    <div className="absolute text-[8px] text-muted-foreground opacity-50">
                      ←motion-note→ Icon pulses once
                    </div>
                  </div>
                  <h3 className="mt-4 text-xl font-semibold">Templates for Every Niche</h3>
                  <p className="mt-2 text-muted-foreground">
                    Start fast with proven formats for stories, listicles, news & more.
                  </p>
                </div>
                {/* Feature 2 */}
                <div>
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {/* Placeholder Icon */} AI
                    <div className="absolute text-[8px] text-muted-foreground opacity-50">
                      ←motion-note→ Icon pulses once
                    </div>
                  </div>
                  <h3 className="mt-4 text-xl font-semibold">Hyper-Realistic AI Voice</h3>
                  <p className="mt-2 text-muted-foreground">
                    110+ languages and accents. Captions burned-in automatically.
                  </p>
                </div>
                {/* Feature 3 */}
                <div>
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {/* Placeholder Icon */} S
                    <div className="absolute text-[8px] text-muted-foreground opacity-50">
                      ←motion-note→ Icon pulses once
                    </div>
                  </div>
                  <h3 className="mt-4 text-xl font-semibold">One-click Scheduling</h3>
                  <p className="mt-2 text-muted-foreground">
                    Connect accounts, set times, and let VidMax handle the posting.
                  </p>
                </div>
              </div>
              <div className="mt-4 text-center text-sm text-muted-foreground">
                SEO-tagged assets saved to your vault.
              </div>
            </div>
          </section>

          {/* Social Proof Bar */}
          <section className="py-12 bg-secondary">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <h3 className="text-center text-lg font-semibold leading-8 text-muted-foreground">
                Trusted by creators on major platforms
              </h3>
              <div className="mt-8 flex items-center justify-center gap-x-8 gap-y-4 flex-wrap">
                {/* Placeholder Logos */}
                <span className="text-muted-foreground/50">Logo 1</span>
                <span className="text-muted-foreground/50">Logo 2</span>
                <span className="text-muted-foreground/50">Logo 3</span>
                <span className="text-muted-foreground/50">Logo 4</span>
              </div>
              <div className="mt-8 text-center text-muted-foreground italic">
                &quot;Quote slider placeholder...&quot;
                <div className="mt-1 text-xs opacity-50">←motion-note→ Fade carousel</div>
              </div>
            </div>
          </section>

          {/* Pricing Teaser */}
          <section className="py-16 sm:py-24 text-center">
            <div className="mx-auto max-w-2xl px-6 lg:px-8">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Ready to Create?</h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Try VidMax for $1 — 60 credits over 3 days, any plan. Cancel anytime.
              </p>
              <div className="mt-8">
                <Button
                  size="lg"
                  onClick={() => {
                    /* noop */
                  }}
                >
                  Start for $1 →
                </Button>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="py-16 sm:py-24 bg-secondary">
            <MarketingFAQ />
          </section>

          {/* Footer CTA (Implicitly handled by MarketingLayout or add specific one if needed) */}
          {/* <section className="py-16 text-center">...</section> */}
        </div>
      </div>
    </>
  )
}

function HowItWorksPage() {
  return (
    <MarketingLayout
      className={cn("bg-background")}
      logoTheme="dark"
      style={
        {
          "--background": "0 0% 0%",
          "--foreground": "0 0% 100%",
          "--secondary": "0 0% 12%",
          "--muted-foreground": "0 0% 50%",
          "--primary-foreground": "0 0% 100%",
          "--muted": "0 0% 12%",
          "--border": "0 0% 8%",
        } as CSSProperties
      }
    >
      <HowItWorks />
    </MarketingLayout>
  )
}

export const Route = createFileRoute("/how-it-works")({
  component: HowItWorksPage,
})
